import React, { useRef, useEffect, useState, useCallback } from 'react';
import type { WheelSegment, SurveyObjectProps } from './types';

/**
 * Scratch Card game — canonical Xotski game mechanic.
 *
 * Design law (game-engine.md §2): the brand is structural, not decorative.
 * The surface the consumer scratches away IS the brand: brand-primary fill +
 * brand logo tiled at 8% opacity. Scratch it off to reveal the prize the
 * engine already chose.
 *
 * Which prize is NOT decided here. The host/wrapper picks the winner with the
 * shared prize engine (calculateWinningSegment, (6-rating)²). This component
 * just reveals `prize` and fires onRevealed.
 *
 * The guest ALWAYS wins something — there is no Try-Again / no-win path.
 *
 * Spec: build-plan §6.1, game-engine §10, brand-customization-protocol.
 */

export type FoilLook = 'solid' | 'metallic' | 'brushed' | 'logo';
export type RevealStyle = 'single' | 'match3';
export type ScratchPhase = 'idle' | 'scratching' | 'nearReveal' | 'autoReveal' | 'winReveal';

/** Resolved 5-color brand (the target BrandKit model). */
export interface ScratchCardBrand {
    primary: string;     // foil fill / structural surface
    secondary: string;   // card border / edges
    accent: string;      // CTA, confetti, progress highlight
    background: string;  // board background
    font: string;        // text on brand surfaces
    logo?: string;       // brand logo (tiled on foil + reveal)
}

export interface ScratchCardProps {
    /** The prize the engine already chose. The guest always wins something. */
    prize: WheelSegment;
    /** Resolved 5-color brand. Use resolveBrandFromSurveyObject() for the 3-color shape. */
    brand: ScratchCardBrand;
    language?: 'en' | 'es';
    /** Foil look (Checkpoint 1 decision). */
    foil?: FoilLook;
    /** single = one big reveal (Path A) · match3 = three choreographed cells (Path B). */
    revealStyle?: RevealStyle;
    /** Brush radius in px (LOCKED 22). */
    scratchRadius?: number;
    /** Auto-reveal fraction cleared (LOCKED 0.45). */
    revealThreshold?: number;
    reducedMotion?: boolean;
    /** Force a visual phase for Storybook state stories (skips interaction). */
    demoPhase?: ScratchPhase;
    /** Fires once when fully revealed with the won prize. */
    onRevealed?: (prize: WheelSegment) => void;
}

const T = {
    scratchHint: { en: 'Scratch to reveal', es: 'Raspa para descubrir' },
    revealBtn: { en: 'Reveal', es: 'Descubrir' },
    done: { en: 'done', es: 'listo' },
} as const;

/**
 * Map the live 3-color `company` shape to the resolved 5-color brand (brand-protocol §2.4).
 *
 * Handles BOTH shapes in the wild:
 *  - raw company settings: `company.primaryColor`
 *  - SurveyRenderer's mapped shape: the brand's primary lands in `company.backgroundColor`
 *
 * The scratch FOIL must stay visible. For dark brands whose primary == background
 * (e.g. Joyly navy), the foil falls back to the accent/secondary so it never
 * blends into the page (still structural brand, just the visible brand color).
 */
export function resolveBrandFromSurveyObject(surveyObject?: SurveyObjectProps): ScratchCardBrand {
    const c: any = surveyObject?.company || {};
    const secondary = c.secondaryColor || '#9333EA';
    const fontColor = c.fontColor || '#FFFFFF';
    const background = surveyObject?.backgroundColor || c.backgroundColor || c.primaryColor || '#1a1a1a';
    let primary = c.primaryColor || c.backgroundColor || surveyObject?.backgroundColor || '#6B21A8';
    // Keep the foil distinct from the board background.
    if (primary.toLowerCase() === background.toLowerCase()) primary = secondary;
    return {
        primary,
        secondary,
        accent: secondary,
        background,
        font: fontColor,
        logo: c.logo,
    };
}

const isLightBg = (font: string) => font === '#000' || font === '#000000';

/** Darken a hex color by a factor (Wheel uses ×0.55 / 45% darker). */
function darken(hex: string, factor = 0.55): string {
    const h = hex.replace('#', '');
    if (h.length < 6) return hex;
    const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    return '#' + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c * factor))).toString(16).padStart(2, '0')).join('');
}
/** Lighten toward white by amount 0..1. */
function lighten(hex: string, amt = 0.35): string {
    const h = hex.replace('#', '');
    if (h.length < 6) return hex;
    const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    return '#' + rgb.map((c) => Math.round(c + (255 - c) * amt).toString(16).padStart(2, '0')).join('');
}

const CARD_W = 300;
const CARD_H = 300;

export const ScratchCard: React.FC<ScratchCardProps> = ({
    prize,
    brand,
    language = 'en',
    foil = 'metallic',
    revealStyle = 'single',
    scratchRadius = 22,
    revealThreshold = 0.45,
    reducedMotion = false,
    demoPhase,
    onRevealed,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const lastPoint = useRef<{ x: number; y: number } | null>(null);
    const sampleTimer = useRef<number | null>(null);
    const revealedFired = useRef(false);

    const [progress, setProgress] = useState(demoPhase === 'idle' || !demoPhase ? 0 : demoPhase === 'scratching' ? 0.2 : demoPhase === 'nearReveal' ? 0.4 : 1);
    const [revealed, setRevealed] = useState(demoPhase === 'winReveal');
    const [fading, setFading] = useState(demoPhase === 'autoReveal');

    // Derived phase (for ARIA + visual cues)
    const phase: ScratchPhase = demoPhase
        ? demoPhase
        : revealed
            ? 'winReveal'
            : fading
                ? 'autoReveal'
                : progress >= 0.35
                    ? 'nearReveal'
                    : progress > 0
                        ? 'scratching'
                        : 'idle';

    // ---- Draw the foil onto the canvas ----
    const paintFoil = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width, h = canvas.height;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, w, h);

        // 'logo' foil: the brand logo IS the scratch surface — one big centered
        // mark on a deepened brand field so the logo always reads with contrast.
        if (foil === 'logo') {
            const g = ctx.createRadialGradient(w / 2, h / 2, h * 0.1, w / 2, h / 2, w * 0.75);
            g.addColorStop(0, darken(brand.primary, 0.85));
            g.addColorStop(1, darken(brand.primary, 0.5));
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
            if (brand.logo) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const maxW = w * 0.74, maxH = h * 0.74;
                    const ratio = Math.min(maxW / img.width, maxH / img.height);
                    const dw = img.width * ratio, dh = img.height * ratio;
                    ctx.save();
                    ctx.globalAlpha = 1;
                    ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
                    ctx.restore();
                };
                img.src = brand.logo;
            }
            return;
        }

        // Base fill by foil look
        if (foil === 'metallic') {
            const g = ctx.createLinearGradient(0, 0, w, h);
            g.addColorStop(0, darken(brand.primary, 0.7));
            g.addColorStop(0.45, lighten(brand.primary, 0.25));
            g.addColorStop(0.55, lighten(brand.primary, 0.4));
            g.addColorStop(1, darken(brand.primary, 0.7));
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
        } else if (foil === 'brushed') {
            ctx.fillStyle = brand.primary;
            ctx.fillRect(0, 0, w, h);
            // Brushed diagonal stripes
            ctx.save();
            ctx.globalAlpha = 0.12;
            ctx.strokeStyle = lighten(brand.primary, 0.6);
            ctx.lineWidth = 2;
            for (let i = -h; i < w; i += 7) {
                ctx.beginPath();
                ctx.moveTo(i, 0);
                ctx.lineTo(i + h, h);
                ctx.stroke();
            }
            ctx.restore();
        } else {
            // solid
            ctx.fillStyle = brand.primary;
            ctx.fillRect(0, 0, w, h);
        }

        // NOTE: the 'logo' foil is fully handled in the early-return block
        // above (a centered brand mark on a deepened field). The duplicate
        // block that used to live here was unreachable dead code — removed.

        // Tiled brand logo watermark at 8% opacity (structural brand)
        ctx.save();
        ctx.globalAlpha = 0.08;
        if (brand.logo) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                ctx.save();
                ctx.globalAlpha = 0.08;
                const size = 56;
                for (let y = 8; y < h; y += size + 18) {
                    for (let x = 8; x < w; x += size + 18) {
                        ctx.drawImage(img, x, y, size, size);
                    }
                }
                ctx.restore();
            };
            img.src = brand.logo;
        } else {
            // No logo → tiled brand-name glyph dots so the surface still reads "branded"
            ctx.fillStyle = brand.font;
            for (let y = 24; y < h; y += 44) {
                for (let x = 24; x < w; x += 44) {
                    ctx.beginPath();
                    ctx.arc(x, y, 5, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        }
        ctx.restore();
    }, [brand, foil]);

    useEffect(() => {
        if (demoPhase && demoPhase !== 'idle' && demoPhase !== 'scratching' && demoPhase !== 'nearReveal') {
            // Fully/near revealed demo states — don't paint a full foil
            return;
        }
        paintFoil();
        revealedFired.current = false;
    }, [paintFoil, demoPhase]);

    // ---- Sample cleared % ----
    const sample = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { width, height } = canvas;
        let cleared = 0;
        const data = ctx.getImageData(0, 0, width, height).data;
        // step for performance
        const step = 16;
        let counted = 0;
        for (let i = 3; i < data.length; i += 4 * step) {
            counted++;
            if (data[i] < 128) cleared++;
        }
        const p = counted ? cleared / counted : 0;
        setProgress(p);
        if (p >= revealThreshold) doAutoReveal();
    }, [revealThreshold]);

    const doAutoReveal = useCallback(() => {
        if (revealedFired.current) return;
        revealedFired.current = true;
        if (sampleTimer.current) { clearInterval(sampleTimer.current); sampleTimer.current = null; }
        setFading(true);
        const finish = () => {
            setRevealed(true);
            setFading(false);
            onRevealed?.(prize);
        };
        if (reducedMotion) finish();
        else window.setTimeout(finish, 320);
    }, [prize, onRevealed, reducedMotion]);

    // ---- Pointer scratching ----
    const pos = (e: React.PointerEvent) => {
        const canvas = canvasRef.current!;
        const r = canvas.getBoundingClientRect();
        return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
    };
    const scratchAt = (x: number, y: number) => {
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = scratchRadius * 2;
        ctx.lineCap = 'round';
        if (lastPoint.current) {
            ctx.beginPath();
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
            ctx.lineTo(x, y);
            ctx.stroke();
        }
        ctx.beginPath();
        ctx.arc(x, y, scratchRadius, 0, Math.PI * 2);
        ctx.fill();
        lastPoint.current = { x, y };
    };
    const onDown = (e: React.PointerEvent) => {
        if (revealed || fading || demoPhase) return;
        drawing.current = true;
        lastPoint.current = null;
        const { x, y } = pos(e);
        scratchAt(x, y);
        canvasRef.current?.setPointerCapture(e.pointerId);
        if (!sampleTimer.current) sampleTimer.current = window.setInterval(sample, 200);
    };
    const onMove = (e: React.PointerEvent) => {
        if (!drawing.current) return;
        const { x, y } = pos(e);
        scratchAt(x, y);
    };
    const onUp = () => { drawing.current = false; lastPoint.current = null; };

    useEffect(() => () => { if (sampleTimer.current) clearInterval(sampleTimer.current); }, []);

    const lightBg = isLightBg(brand.font);
    const dimText = lightBg ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.6)';
    const progressColor = phase === 'nearReveal' ? brand.accent : dimText;

    // ---- Prize under-layer ----
    const prizeName = language === 'es' ? (prize?.nameTranslate || prize?.name) : prize?.name;
    const stars = (prize as any)?.stars ?? prize?.rating ?? 0;
    const prizeImg = prize?.image || prize?.logo;

    const renderUnder = () => {
        if (revealStyle === 'match3') {
            // Engine-first choreographed row: three of the won prize (guest always wins).
            const cells = [prize, prize, prize];
            return (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                    {cells.map((p, i) => (
                        <div key={i} style={{
                            width: 74, height: 92, borderRadius: 12, background: 'rgba(255,255,255,0.06)',
                            border: `1px solid ${brand.accent}`, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 4,
                            animation: reducedMotion ? undefined : `winPop .4s ${i * 0.12}s both`,
                        }}>
                            {prizeImg ? <img src={prizeImg} alt="" style={{ width: 42, height: 42, objectFit: 'contain' }} />
                                : <span style={{ fontSize: 34 }}>{p?.emoji || '🎁'}</span>}
                            <span style={{ fontSize: 9, color: brand.accent, fontWeight: 700 }}>{'★'.repeat(Math.max(1, Math.min(5, stars)))}</span>
                        </div>
                    ))}
                </div>
            );
        }
        // single big reveal
        return (
            <div style={{ textAlign: 'center', color: '#fff', padding: 16 }}>
                {prizeImg
                    ? <img src={prizeImg} alt={prizeName} style={{ width: 120, height: 120, objectFit: 'contain', margin: '0 auto 8px' }} />
                    : <div style={{ fontSize: 80, marginBottom: 4 }}>{prize?.emoji || '🎁'}</div>}
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 1, color: '#fff' }}>{prizeName}</div>
                <div style={{ fontSize: 16, color: brand.accent, marginTop: 2 }}>{'★'.repeat(Math.max(1, Math.min(5, stars)))}</div>
            </div>
        );
    };

    const shimmerOn = foil === 'metallic' && !reducedMotion && !revealed && !demoPhase;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            {/* Card */}
            <div
                role="img"
                aria-label={
                    phase === 'winReveal'
                        ? `You won ${prizeName}`
                        : T.scratchHint[language]
                }
                style={{
                    position: 'relative', width: CARD_W, height: CARD_H, borderRadius: 18,
                    overflow: 'hidden', border: `3px solid ${brand.secondary}`,
                    boxShadow: `0 12px 40px ${darken(brand.primary, 0.4)}66`,
                    background: '#1a1a1a', // neutral under-layer (canon)
                    touchAction: 'none', userSelect: 'none',
                }}
            >
                {/* Prize under-layer */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {renderUnder()}
                </div>

                {/* Foil canvas (the brand you scratch off) */}
                {!revealed && (
                    <canvas
                        ref={canvasRef}
                        width={CARD_W}
                        height={CARD_H}
                        onPointerDown={onDown}
                        onPointerMove={onMove}
                        onPointerUp={onUp}
                        onPointerLeave={onUp}
                        style={{
                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                            cursor: demoPhase ? 'default' : 'grab',
                            opacity: fading ? 0 : 1,
                            transition: reducedMotion ? undefined : 'opacity .32s ease',
                        }}
                    />
                )}

                {/* Metallic shimmer (fades as you scratch; pointer-none) */}
                {shimmerOn && (
                    <div aria-hidden style={{
                        position: 'absolute', top: 0, left: 0, width: '40%', height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent)',
                        transform: 'rotate(14deg)', pointerEvents: 'none',
                        opacity: Math.max(0, 1 - progress * 2.2),
                        animation: 'sheen 2.6s ease-in-out infinite',
                    }} />
                )}

                {/* Idle hint */}
                {phase === 'idle' && (
                    <div className="hint-pulse" aria-hidden style={{
                        position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center',
                        color: brand.font, fontSize: 13, fontWeight: 600, pointerEvents: 'none',
                        textShadow: '0 1px 4px rgba(0,0,0,.4)',
                    }}>
                        👆 {T.scratchHint[language]}
                    </div>
                )}
            </div>

            {/* Progress + reveal fallback (accessibility) */}
            {!revealed && !demoPhase && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: CARD_W }}>
                    <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: progressColor }}>
                        {Math.round(progress * 100)}% {T.done[language]}
                    </div>
                    <button
                        type="button"
                        onClick={doAutoReveal}
                        style={{
                            border: `1.5px solid ${brand.accent}`, color: brand.accent, background: 'transparent',
                            borderRadius: 10, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            fontFamily: "'DM Sans', sans-serif",
                        }}
                    >
                        {T.revealBtn[language]}
                    </button>
                </div>
            )}

            {/* Powered by Xotski — 8px, bottom-center, non-configurable */}
            <div aria-hidden style={{ fontSize: 8, letterSpacing: 0.5, color: dimText, marginTop: 2 }}>
                Powered by Xotski
            </div>
        </div>
    );
};

export default ScratchCard;
